/**
 * AgencySettings — premium dark Account Settings hub at /agency/settings.
 * Mirrors the Admin Command Center design language. Live data from
 * GET /agency/profile (now degrades gracefully instead of 404) and
 * GET /agency/billing/status. Skeleton loaders + retryable error state.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SettingsShell from '../../components/settings/SettingsShell';
import LinkedAccountsPanel from '../../components/LinkedAccountsPanel';
import { SecurityPanel } from '../../components/settings/SecurityPanel';
import {
  HubCard, ActionRow, StatCard, StatusPill, SkeletonRow, ErrorState, IconChip,
} from '../../components/settings/HubKit';
import { api } from '../../utils/api';
import {
  User, Briefcase, Users, CreditCard, Lock, Trash2, Link2, LifeBuoy,
  LayoutDashboard, UserSearch, Megaphone, Building2, BadgeCheck, ArrowRight, Palette,
} from 'lucide-react';
import ThemePicker from '../../components/settings/ThemePicker';

const SECTIONS = [
  { id: 'profile',        label: 'Agency profile',  icon: Briefcase },
  { id: 'appearance',     label: 'Appearance',      icon: Palette },
  { id: 'roster',         label: 'Roster & hiring', icon: Users },
  { id: 'billing',        label: 'Billing',         icon: CreditCard },
  { id: 'security',       label: 'Security',        icon: Lock },
  { id: 'linked-accounts',label: 'Linked accounts', icon: Link2 },
  { id: 'danger',         label: 'Danger zone',     icon: Trash2 },
];

const DashboardActionCard = () => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/agency/dashboard')}
      data-testid="agency-settings-dashboard"
      className="group relative w-full overflow-hidden rounded-2xl p-5 text-left transition-all
                 border border-cyan-500/30 bg-gradient-to-br from-slate-950 via-slate-900/90 to-slate-950
                 hover:border-cyan-400/50 hover:shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-0.5
                 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70"
    >
      <div aria-hidden="true" className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-500/10 blur-3xl group-hover:bg-cyan-500/15 transition-colors" />
      <div className="relative flex items-center gap-4">
        <IconChip icon={LayoutDashboard} accent="cyan" size="lg" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-100">Agency Dashboard</div>
          <div className="text-xs text-slate-400 mt-0.5">Jobs, pipeline, and workspace overview</div>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-300 group-hover:gap-2 transition-all">
          Open <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </button>
  );
};

const ProfilePanel = ({ profile, billing }) => {
  const billingTone = billing?.billing_status === 'past_due' ? 'rose' : 'emerald';
  return (
    <div className="space-y-4" data-testid="agency-settings-profile">
      {profile?.profile_missing && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="agency-profile-missing">
          Your agency profile isn't finished yet — head to your dashboard to complete setup.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard icon={Building2} accent="cyan" label="Company" value={profile?.company_name || 'Not set'} testid="agency-stat-company" />
        <StatCard icon={User} accent="purple" label="Primary contact" value={profile?.contact_name || 'Not set'} testid="agency-stat-contact" />
        <StatCard icon={Users} accent="teal" label="Active roster" value={profile?.roster_count ?? 0} testid="agency-stat-roster" />
        <StatCard icon={Megaphone} accent="gold" label="Open jobs" value={profile?.stats?.active_jobs ?? 0} testid="agency-stat-jobs" />
        <StatCard
          icon={CreditCard}
          accent={billingTone}
          label="Billing status"
          value={<span className="capitalize">{billing?.billing_status || 'active'}</span>}
          testid="agency-stat-billing"
        />
        <StatCard
          icon={BadgeCheck}
          accent={billing?.is_restricted ? 'rose' : 'emerald'}
          label="Account status"
          value={billing?.is_restricted ? 'Restricted' : 'In good standing'}
          badge={billing?.is_restricted
            ? <StatusPill tone="rose" label="Action Needed" testid="agency-account-pill" />
            : <StatusPill tone="emerald" label="Active" testid="agency-account-pill" />}
          testid="agency-stat-account"
        />
      </div>
      <DashboardActionCard />
    </div>
  );
};

const BillingPanel = ({ profile, billing }) => (
  <HubCard title="Billing & subscription" sub="Plan, payment health, and restrictions." testid="agency-settings-billing">
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Current plan</div>
        <div className="text-xl font-bold text-slate-100 capitalize" data-testid="agency-billing-plan">
          {profile?.subscription?.plan_name || profile?.subscription?.plan_id || 'Free'}
        </div>
      </div>
      <StatusPill
        tone={billing?.billing_status === 'past_due' ? 'rose' : 'emerald'}
        label={billing?.billing_status || 'active'}
        testid="agency-billing-status"
      />
    </div>
    {billing?.is_restricted && (
      <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200" data-testid="agency-billing-restricted">
        {billing.restriction_reason || 'Your account is restricted due to a billing issue.'}
      </div>
    )}
    <p className="text-xs text-slate-500">
      Plan changes and payment methods are handled from your dashboard's
      subscription flow. Contact support for invoicing questions.
    </p>
  </HubCard>
);

const AgencySettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(searchParams.get('section') || 'profile');
  const [profile, setProfile] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [p, b] = await Promise.allSettled([
        api.get('/agency/profile'),
        api.get('/agency/billing/status'),
      ]);
      if (p.status === 'fulfilled') setProfile(p.value.data);
      else setLoadError(true);
      if (b.status === 'fulfilled') setBilling(b.value.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSection = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    next.set('section', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <SettingsShell
      role="agency"
      variant="dark"
      sections={SECTIONS}
      activeSection={active}
      onSectionChange={handleSection}
    >
      {active === 'profile' && (
        loading ? (
          <div className="space-y-3" data-testid="agency-settings-loading">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <SkeletonRow /><SkeletonRow /><SkeletonRow />
              <SkeletonRow /><SkeletonRow /><SkeletonRow />
            </div>
          </div>
        ) : loadError ? (
          <ErrorState message="We couldn't load your agency profile." onRetry={load} testid="agency-profile-error" />
        ) : (
          <ProfilePanel profile={profile} billing={billing} />
        )
      )}
      {active === 'appearance' && (
        <HubCard title="Appearance" sub="Choose how iEvntür looks for you — everywhere you sign in." testid="agency-settings-appearance">
          <ThemePicker />
        </HubCard>
      )}
      {active === 'roster' && (
        <HubCard title="Roster & hiring" sub="Your talent, your pipeline, your next hires." testid="agency-settings-roster">
          <ActionRow icon={Users} accent="teal" label="My roster" sub="Talent you manage and their statuses" to="/agency/roster" testid="agency-settings-roster-link" />
          <ActionRow icon={UserSearch} accent="cyan" label="Talent pool" sub="Browse and recruit new talent to your roster" to="/agency/talent-pool" testid="agency-settings-talent-pool" />
          <ActionRow icon={Megaphone} accent="gold" label="Recruitment" sub="Job posts, applications, and pipeline" to="/agency/recruitment" testid="agency-settings-recruitment" />
          <ActionRow icon={User} accent="purple" label="Talent hub" sub="Shared workspace with your talent" to="/agency/talent-hub" testid="agency-settings-talent-hub" />
        </HubCard>
      )}
      {active === 'billing' && (
        loading
          ? <HubCard title="Billing & subscription" testid="agency-settings-billing"><SkeletonRow /><SkeletonRow /></HubCard>
          : <BillingPanel profile={profile} billing={billing} />
      )}
      {active === 'security' && <SecurityPanel />}
      {active === 'linked-accounts' && <LinkedAccountsPanel />}
      {active === 'danger' && (
        <HubCard title="Danger zone" sub="Irreversible workspace actions." testid="agency-settings-danger">
          <p className="text-sm text-slate-400">
            Closing your agency workspace affects your roster and any active
            job posts, so our team handles it manually — usually within one
            business day.
          </p>
          <ActionRow icon={LifeBuoy} accent="rose" label="Request workspace closure" sub="Open a support ticket and we'll take care of it" to="/client/help" testid="agency-settings-close" />
        </HubCard>
      )}
    </SettingsShell>
  );
};

export default AgencySettings;
